import { announce } from "@kovernow/flows/announce";
import {
  createSignUpFlow,
  type SignUpFlow,
  type SignUpState,
  type SocialHandoff,
} from "@kovernow/flows/auth";
import { Button } from "@kovernow/react-ui/components/button";
import { Checkbox } from "@kovernow/react-ui/components/checkbox";
import { Info } from "lucide-react";
import { useEffect, useId, useMemo, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useServices } from "../core/app-context";
import { useMutationGate } from "../core/mutation-gate";
import { partnerStorage } from "../core/partner-storage";
import { useOnLeave } from "../core/use-on-leave";
import { useT } from "../core/use-t";
import { FormField } from "../components/FormField";
import { GatedButton } from "../components/GatedButton";
import { OtpStep } from "../components/OtpStep";
import { Page } from "../components/Page";
import { PasswordField } from "../components/PasswordField";
import { Recovery } from "../components/Recovery";

const STEP_TITLES: Record<SignUpState["step"], string> = {
  form: "auth.signUp.title",
  otp: "auth.otp.title",
  exists: "auth.exists.title",
  done: "auth.signUp.title",
};

/** "google.com" → i18n key for the provider's name. */
export function providerKey(providerId: string | undefined): string {
  if (providerId === "google.com") return "auth.providers.google";
  if (providerId === "apple.com") return "auth.providers.apple";
  return "auth.providers.other";
}

function readSocial(state: unknown): SocialHandoff | undefined {
  const s = (state as { social?: Partial<SocialHandoff> } | null)?.social;
  return s && typeof s.uid === "string" && typeof s.email === "string"
    ? { uid: s.uid, email: s.email, displayName: s.displayName, providerId: s.providerId }
    : undefined;
}

/**
 * Sign-up (W-1): rendered only from the flows sign-up machine. A Google/Apple
 * identity arrives from sign-in's notFound step in router state; a partner
 * code from `/p/<code>` (sessionStorage) or `?partner=unknown`.
 */
export function SignUpScreen({ flowOverride }: { flowOverride?: SignUpFlow }) {
  const services = useServices();
  const location = useLocation();
  const [params] = useSearchParams();
  const t = useT();
  const navigate = useNavigate();
  const flow = useMemo(
    () =>
      flowOverride ??
      createSignUpFlow({
        identity: services.identity,
        api: services.authApi,
        session: services.session,
        orgId: services.config.orgId,
        partnerCode: partnerStorage.get() ?? undefined,
        partnerUnknown: params.get("partner") === "unknown",
        social: readSocial(location.state),
        recovery: { hasMessage: (k) => services.i18n.exists(k) },
      }),
    // Seeded once per visit; later URL/state changes don't rebuild the flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flowOverride, services],
  );
  const state = useSyncExternalStore(flow.subscribe, flow.getState);

  useOnLeave(() => void flow.abandon());

  useEffect(() => {
    if (state.step === "done") {
      partnerStorage.clear();
      announce(t("auth.signUp.created"));
      navigate("/", { replace: true });
    }
  }, [state.step, navigate, t]);

  const toSignIn = () => navigate(`/sign-in?email=${encodeURIComponent(state.email)}`);

  return (
    <Page
      titleKey={STEP_TITLES[state.step]}
      flowKey="flows.signUp"
      focusKey={state.step === "form" ? undefined : state.step}
      className="mx-auto w-full max-w-md px-4 py-8"
    >
      <div aria-live="polite">
        {state.error ? (
          <Recovery
            live={false}
            action={state.error}
            onRetry={() => flow.dismissError()}
            handlers={{ resend_code: () => void flow.resendOtp(), sign_in: toSignIn }}
          />
        ) : null}
      </div>
      <div className="mt-4">
        {state.step === "form" || state.step === "done" ? <SignUpForm flow={flow} state={state} /> : null}
        {state.step === "otp" ? (
          <OtpStep
            intro={t("auth.otp.intro", { email: state.email })}
            otpResetCount={state.otpResetCount}
            busy={state.busy}
            error={state.fieldErrors.otp ? t(state.fieldErrors.otp) : undefined}
            resendAvailableAt={state.resendAvailableAt}
            onSubmit={(code) => void flow.submitOtp(code)}
            onResend={() => void flow.resendOtp()}
            onBack={() => flow.back()}
            backLabel={t("auth.otp.useDifferentEmail")}
          />
        ) : null}
        {state.step === "exists" ? (
          <div className="flex flex-col gap-6">
            <p>{t("auth.exists.body", { email: state.email })}</p>
            <Button type="button" variant="cta" size="touch" onClick={toSignIn}>
              {t("auth.exists.signIn")}
            </Button>
            <Button type="button" variant="link" className="self-start px-0" onClick={() => flow.back()}>
              {t("auth.otp.useDifferentEmail")}
            </Button>
          </div>
        ) : null}
      </div>
    </Page>
  );
}

/** Checkbox row with a ≥ 44px label target. */
function CheckRow({
  checked,
  onChange,
  label,
  error,
  name,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  error?: string;
  name: string;
}) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-h-11 items-start gap-3 py-1">
        <Checkbox
          id={id}
          name={name}
          className="mt-0.5 size-5 border-input"
          checked={checked}
          onCheckedChange={(v) => onChange(v === true)}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
        />
        <label htmlFor={id} className="min-h-11 flex-1 text-sm leading-6">
          {label}
        </label>
      </div>
      {error ? (
        <p id={errorId} className="text-sm font-semibold text-status-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  const t = useT();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline">
      {children}
      <span className="sr-only"> {t("auth.signUp.opensNewTab")}</span>
    </a>
  );
}

function SignUpForm({ flow, state }: { flow: SignUpFlow; state: SignUpState }) {
  const t = useT();
  const gate = useMutationGate();
  const { config } = useServices();
  const social = state.mode === "social";
  const provider = t(providerKey(state.providerId));
  const [firstName, setFirstName] = useState(state.firstName);
  const [familyName, setFamilyName] = useState(state.familyName);
  const [email, setEmail] = useState(state.email);
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const flagged = (f: string) => (state.error?.fields?.includes(f) || state.fieldErrors[f as keyof SignUpState["fieldErrors"]] ? true : undefined);
  const err = (f: keyof SignUpState["fieldErrors"]) => (state.fieldErrors[f] ? t(state.fieldErrors[f]!) : undefined);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (gate.blocked || state.busy || !terms) return;
    void flow.submit({ firstName, familyName, email, password, termsAccepted: terms, marketingConsent: marketing });
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-muted-foreground">
        {social ? t("auth.signUp.socialIntro", { provider }) : t("auth.signUp.intro")}
      </p>
      {state.partnerUnknown ? (
        <div role="status" className="flex gap-3 rounded-md border border-status-info p-3 text-sm" data-testid="partner-unknown">
          <Info aria-hidden="true" className="size-5 shrink-0 text-status-info" />
          <p>{t("auth.signUp.partnerUnknown")}</p>
        </div>
      ) : null}
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <FormField
          label={t("auth.signUp.firstName")}
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={() => firstName && flow.validateField("firstName", firstName)}
          error={err("firstName")}
          aria-invalid={flagged("firstName")}
        />
        <FormField
          label={t("auth.signUp.familyName")}
          name="familyName"
          autoComplete="family-name"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          onBlur={() => familyName && flow.validateField("familyName", familyName)}
          error={err("familyName")}
          aria-invalid={flagged("familyName")}
        />
        <FormField
          label={t("auth.signUp.email")}
          name="email"
          type="email"
          autoComplete="email"
          value={social ? state.email : email}
          readOnly={social}
          hint={social ? t("auth.signUp.emailLocked", { provider }) : undefined}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => !social && email && flow.validateField("email", email)}
          error={err("email")}
          aria-invalid={flagged("email")}
        />
        {social ? null : (
          <PasswordField
            label={t("auth.signUp.password")}
            name="password"
            autoComplete="new-password"
            hint={t("auth.signUp.passwordHint")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => password && flow.validateField("password", password)}
            error={err("password")}
            aria-invalid={flagged("password")}
          />
        )}
        {state.partnerCode ? (
          <FormField
            label={t("auth.signUp.partner")}
            name="partnerCode"
            value={state.partnerCode}
            readOnly
            hint={t("auth.signUp.partnerHint")}
          />
        ) : null}
        <CheckRow
          name="terms"
          checked={terms}
          onChange={setTerms}
          error={err("terms")}
          label={
            <>
              {t("auth.signUp.terms")}{" "}
              <ExternalLink href={config.brand.legal.termsUrl}>{t("auth.signUp.termsLink")}</ExternalLink>{" "}
              {t("auth.signUp.termsAnd")}{" "}
              <ExternalLink href={config.brand.legal.privacyUrl}>{t("auth.signUp.privacyLink")}</ExternalLink>
            </>
          }
        />
        <CheckRow name="marketing" checked={marketing} onChange={setMarketing} label={t("auth.signUp.marketing")} />
        <GatedButton
          type="submit"
          variant="cta"
          busy={state.busy}
          label={t("auth.signUp.submit")}
          unavailableReason={terms ? undefined : t("auth.signUp.termsRequiredReason")}
        />
      </form>
      <p className="text-sm">
        {t("auth.signUp.haveAccount")}{" "}
        <Link to="/sign-in" className="inline-flex min-h-11 items-center font-semibold text-primary underline">
          {t("auth.signUp.signInLink")}
        </Link>
      </p>
    </div>
  );
}
